import React, { useCallback, useState } from 'react';
import { Activity, Save, RefreshCw, FileText, Calculator } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import PersonalParametersSection from './PersonalParametersSection';
import BodyCompositionSection from './BodyCompositionSection';
import ActivityLevelSection from './ActivityLevelSection';
import GoalSection from './GoalSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';

/**
 * Компонент основной формы расчета базального метаболизма
 */
const BmrCalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  activityLevels,
  bmrFormulas,
  dietTypes,
  saveProfile,
  setShowMealPlan,
  showMealPlan
}) => {
  
  // Состояние для имени сохраняемого профиля
  const [saveProfileName, setSaveProfileName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };
      
      // Проверка необходимости поля bodyFat для формулы Кэтча-МакАрдла
      if (name === 'formula' && value !== 'katch') {
        // Если не выбрана формула Кэтча, сбрасываем поле bodyFat
        newState.bodyFat = '';
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      gender, age, weight, height, activityLevel, formula, bodyFat,
      goal, weightGoal, mealCount, dietType
    } = formData;

    // Парсинг числовых значений
    const numAge = parseIntOrDefault(age, -1);
    const numWeight = parseFloatOrDefault(weight, -1);
    const numHeight = parseIntOrDefault(height, -1);
    const numBodyFat = bodyFat ? parseFloatOrDefault(bodyFat, -1) : 0;
    const numWeightGoal = parseFloatOrDefault(weightGoal, -1);
    const numMealCount = parseIntOrDefault(mealCount, -1);

    // Валидация основных полей
    if (!gender) newErrors.gender = 'Выберите пол.';
    
    if (numAge <= 0) newErrors.age = 'Возраст должен быть положительным числом.';
    if (!newErrors.age && numAge > 120) newErrors.age = 'Максимальный возраст 120 лет.';
    
    if (numWeight <= 0) newErrors.weight = 'Вес должен быть положительным числом.';
    if (!newErrors.weight && numWeight > 350) newErrors.weight = 'Максимальный вес 350 кг.';
    
    if (numHeight <= 0) newErrors.height = 'Рост должен быть положительным числом.';
    if (!newErrors.height && numHeight > 250) newErrors.height = 'Максимальный рост 250 см.';
    
    if (!activityLevel) newErrors.activityLevel = 'Выберите уровень активности.';
    
    if (!formula) newErrors.formula = 'Выберите формулу расчета.';
    
    // Если выбрана формула Кэтча-МакАрдла, проверяем процент жира
    if (formula === 'katch') {
      if (!bodyFat) {
        newErrors.bodyFat = 'Для формулы Кэтча-МакАрдла необходимо указать процент жира.';
      } else if (numBodyFat <= 0 || numBodyFat >= 100) {
        newErrors.bodyFat = 'Процент жира должен быть от 1 до 99%.';
      }
    }

    // Валидация целей
    if (!goal) newErrors.goal = 'Выберите цель.';
    
    if ((goal === 'lose' || goal === 'gain') && (numWeightGoal <= 0)) {
      newErrors.weightGoal = 'Укажите желаемое изменение веса за неделю.';
    }
    
    if ((goal === 'lose' || goal === 'gain') && (numWeightGoal > 2)) {
      newErrors.weightGoal = 'Рекомендуем не более 2 кг в неделю.';
    }
    
    if (numMealCount <= 0 || numMealCount > 10) {
      newErrors.mealCount = 'Количество приемов пищи должно быть от 1 до 10.';
    }
    
    if (!dietType) newErrors.dietType = 'Выберите тип питания.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Функция расчета BMR (базального метаболизма)
  const calculateBMR = (gender, age, weight, height, bodyFat, formula) => {
    // Числовые значения для расчетов
    const numAge = parseIntOrDefault(age, 30);
    const numWeight = parseFloatOrDefault(weight, 70);
    const numHeight = parseIntOrDefault(height, 170);
    const numBodyFat = bodyFat ? parseFloatOrDefault(bodyFat, 20) : null;

    let bmr = 0;

    switch (formula) {
      case 'mifflin':
        // Формула Миффлина-Сан Жеора
        if (gender === 'male') {
          bmr = 10 * numWeight + 6.25 * numHeight - 5 * numAge + 5;
        } else {
          bmr = 10 * numWeight + 6.25 * numHeight - 5 * numAge - 161;
        }
        break;
      
      case 'harris':
        // Формула Харриса-Бенедикта
        if (gender === 'male') {
          bmr = 88.362 + (13.397 * numWeight) + (4.799 * numHeight) - (5.677 * numAge);
        } else {
          bmr = 447.593 + (9.247 * numWeight) + (3.098 * numHeight) - (4.330 * numAge);
        }
        break;
      
      case 'katch':
        // Формула Кэтча-МакАрдла (требует % жира в теле)
        if (numBodyFat !== null) {
          const leanBodyMass = numWeight * (1 - (numBodyFat / 100));
          bmr = 370 + (21.6 * leanBodyMass);
        } else {
          // Если процент жира не указан, используем формулу Миффлина как резервную
          if (gender === 'male') {
            bmr = 10 * numWeight + 6.25 * numHeight - 5 * numAge + 5;
          } else {
            bmr = 10 * numWeight + 6.25 * numHeight - 5 * numAge - 161;
          }
        }
        break;
      
      default:
        // По умолчанию используем формулу Миффлина-Сан Жеора
        if (gender === 'male') {
          bmr = 10 * numWeight + 6.25 * numHeight - 5 * numAge + 5;
        } else {
          bmr = 10 * numWeight + 6.25 * numHeight - 5 * numAge - 161;
        }
    }

    return Math.round(bmr);
  };

  // Функция расчета дневной нормы калорий на основе BMR и уровня активности
  const calculateTDEE = (bmr, activityLevel) => {
    // Находим коэффициент активности
    const activity = activityLevels.find(level => level.id === activityLevel);
    const activityFactor = activity ? activity.factor : 1.2; // По умолчанию сидячий образ жизни

    return Math.round(bmr * activityFactor);
  };

  // Расчет калорий с учетом цели (похудение, поддержание веса, набор массы)
  const calculateTargetCalories = (tdee, goal, weightGoal) => {
    const numWeightGoal = parseFloatOrDefault(weightGoal, 0.5);
    
    // 1 кг жира ≈ 7700 ккал
    const caloriesPerKg = 7700;
    const caloriesPerWeek = numWeightGoal * caloriesPerKg;
    const caloriesPerDay = Math.round(caloriesPerWeek / 7);
    
    let targetCalories = tdee;
    
    if (goal === 'lose') {
      targetCalories = Math.max(1200, tdee - caloriesPerDay); // Минимум 1200 ккал
    } else if (goal === 'gain') {
      targetCalories = tdee + caloriesPerDay;
    }
    
    return Math.round(targetCalories);
  };

  // Расчет макронутриентов на основе целевых калорий и типа диеты
  const calculateMacros = (targetCalories, dietType) => {
    // Находим выбранный тип диеты
    const diet = dietTypes.find(d => d.id === dietType) || dietTypes[0];
    
    // Калорийность нутриентов: белки и углеводы - 4 ккал/г, жиры - 9 ккал/г
    const proteinCaloriesPerGram = 4;
    const carbsCaloriesPerGram = 4;
    const fatCaloriesPerGram = 9;
    
    // Распределение калорий по макронутриентам
    const proteinCalories = targetCalories * diet.macro.protein;
    const fatCalories = targetCalories * diet.macro.fat;
    const carbsCalories = targetCalories * diet.macro.carbs;
    
    // Перевод калорий в граммы
    const protein = Math.round(proteinCalories / proteinCaloriesPerGram);
    const fat = Math.round(fatCalories / fatCaloriesPerGram);
    const carbs = Math.round(carbsCalories / carbsCaloriesPerGram);
    
    return {
      protein,
      fat,
      carbs,
      proteinPercent: Math.round(diet.macro.protein * 100),
      fatPercent: Math.round(diet.macro.fat * 100),
      carbsPercent: Math.round(diet.macro.carbs * 100)
    };
  };

  // Расчет ИМТ (индекса массы тела)
  const calculateBMI = (weight, height) => {
    const numWeight = parseFloatOrDefault(weight, 70);
    const numHeight = parseIntOrDefault(height, 170) / 100; // в метрах
    
    if (numHeight <= 0) return 0;
    
    const bmi = numWeight / (numHeight * numHeight);
    return parseFloat(bmi.toFixed(1));
  };

  // Функция для получения категории ИМТ
  const getBMICategory = (bmi) => {
    if (bmi < 16) {
      return { 
        category: 'Выраженный дефицит массы тела',
        color: 'orange',
        recommendation: 'Срочно обратитесь к врачу для обследования.'
      };
    } else if (bmi < 18.5) {
      return { 
        category: 'Недостаточная масса тела',
        color: 'yellow',
        recommendation: 'Рекомендуется набор веса под наблюдением специалиста.'
      };
    } else if (bmi < 25) {
      return { 
        category: 'Нормальная масса тела',
        color: 'green',
        recommendation: 'Поддерживайте текущий вес и здоровый образ жизни.'
      };
    } else if (bmi < 30) {
      return { 
        category: 'Избыточная масса тела (предожирение)',
        color: 'yellow',
        recommendation: 'Рекомендуется постепенное снижение веса.'
      };
    } else if (bmi < 35) {
      return { 
        category: 'Ожирение I степени',
        color: 'orange',
        recommendation: 'Необходимо снижение веса. Консультация с врачом.'
      };
    } else if (bmi < 40) {
      return { 
        category: 'Ожирение II степени',
        color: 'red',
        recommendation: 'Требуется программа по снижению веса под наблюдением врача.'
      };
    } else {
      return { 
        category: 'Ожирение III степени',
        color: 'darkred',
        recommendation: 'Немедленно обратитесь к врачу. Высокий риск для здоровья.'
      };
    }
  };

  // Функция выполнения расчета
  const performCalculation = useCallback(() => {
    if (!validateForm()) {
      // Прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setShowMealPlan(false);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // Расчет BMR (базальный метаболизм)
        const bmr = calculateBMR(
          formData.gender,
          formData.age,
          formData.weight,
          formData.height,
          formData.bodyFat,
          formData.formula
        );

        // Расчет TDEE (общий расход энергии)
        const tdee = calculateTDEE(bmr, formData.activityLevel);

        // Расчет целевых калорий с учетом цели
        const targetCalories = calculateTargetCalories(
          tdee,
          formData.goal,
          formData.weightGoal
        );

        // Расчет макронутриентов
        const macros = calculateMacros(targetCalories, formData.dietType);

        // Расчет ИМТ и определение категории
        const bmi = calculateBMI(formData.weight, formData.height);
        const bmiCategory = getBMICategory(bmi);

        // Расчет распределения калорий по приемам пищи
        const numMealCount = parseIntOrDefault(formData.mealCount, 3);
        const includeSnacks = formData.includeSnacks;
        
        // Основные приемы пищи (завтрак, обед, ужин)
        const mainMeals = Math.min(3, numMealCount);
        const remainingMeals = numMealCount - mainMeals;
        
        // Распределение калорий по приемам пищи
        const mealDistribution = [];
        
        // Процентное распределение для основных приемов пищи
        const mainMealPercents = [0.3, 0.4, 0.3]; // завтрак 30%, обед 40%, ужин 30%
        let remainingCaloriesPercent = 0;
        
        // Распределяем калории на основные приемы пищи
        for (let i = 0; i < mainMeals; i++) {
          const percent = mainMealPercents[i] || 0.33;
          mealDistribution.push({
            name: i === 0 ? 'Завтрак' : i === 1 ? 'Обед' : 'Ужин',
            calories: Math.round(targetCalories * percent * 0.85), // 85% на основные приемы
            isMain: true
          });
          remainingCaloriesPercent += percent;
        }
        
        // Если включены перекусы, добавляем их
        if (includeSnacks && remainingMeals > 0) {
          const snackCaloriesTotal = targetCalories * 0.15; // 15% на перекусы
          const snackCaloriesEach = snackCaloriesTotal / remainingMeals;
          
          for (let i = 0; i < remainingMeals; i++) {
            mealDistribution.push({
              name: `Перекус ${i + 1}`,
              calories: Math.round(snackCaloriesEach),
              isMain: false
            });
          }
        }
        
        // Итоговые результаты расчета
        const calculationResults = {
          bmr,
          tdee,
          targetCalories,
          bmi,
          bmiCategory,
          macros,
          mealDistribution,
          // Дополнительная информация
          waterRecommendation: Math.round(formData.weight * 0.03 * 1000) / 1000, // 30 мл на кг веса
          proteinRecommendation: Math.round(parseFloatOrDefault(formData.weight) * (formData.goal === 'gain' ? 2.0 : 1.6)), // г белка на кг веса
          activityFactor: activityLevels.find(level => level.id === formData.activityLevel)?.factor || 1.2
        };

        setResults(calculationResults);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные.` });
        setResults(null);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, activityLevels, dietTypes, setIsCalculating, setShowMealPlan]);

  // Сброс формы
  const resetForm = () => {
    setFormData({
      gender: 'female',
      age: '30',
      weight: '65',
      height: '165',
      activityLevel: 'light',
      formula: 'mifflin',
      bodyFat: '',
      goal: 'maintain',
      weightGoal: '0.5',
      mealCount: '3',
      includeSnacks: true,
      dietType: 'balanced',
    });
    setResults(null);
    setErrors({});
    setSaveProfileName('');
    setShowSaveDialog(false);
    setShowMealPlan(false);
  };

  // Обработчик сохранения профиля
  const handleSaveProfile = () => {
    if (showSaveDialog) {
      const savedResult = saveProfile(saveProfileName || null);
      if (savedResult) {
        alert(`Профиль "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveProfileName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    performCalculation();
  };

  // Обработчик отображения плана питания
  const handleShowMealPlan = () => {
    setShowMealPlan(true);
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Activity className="w-6 h-6 text-emerald-500" />
          Расчет базального метаболизма
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Основные персональные параметры */}
        <PersonalParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
        />

        {/* Уровень активности */}
        <ActivityLevelSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          activityLevels={activityLevels}
        />

        {/* Формула расчета BMR и состав тела */}
        <BodyCompositionSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          bmrFormulas={bmrFormulas}
        />

        {/* Цели и тип питания */}
        <GoalSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          dietTypes={dietTypes}
        />

        {/* Диалог сохранения профиля */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveProfileName}
                onChange={(e) => setSaveProfileName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveProfile}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<Calculator className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Расчет...' : 'Рассчитать метаболизм'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && !showMealPlan && (
            <Button
              type="button"
              onClick={handleShowMealPlan}
              icon={<FileText className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Показать план питания
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveProfile}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить профиль
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">
            Пожалуйста, исправьте ошибки в форме.
          </p>
        )}
      </div>
    </form>
  );
};

export default BmrCalculationForm;